import { TDemoAIFillingSubstep, TDemoAIFillingGroupedStep } from '@commenda/types';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Icons,
} from '@commenda/kakashi';
import Lottie from 'lottie-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import commendaLoadingAnimation from '@commenda/kakashi/src/components/Loading/commenda-loading-animation.json';

export type TTimelineTextStep = {
  type: 'text';
  title: string;
  description: string;
};

export function TextStepItem({
  step,
  isCompleted,
  isCurrent,
  isLast,
}: {
  step: TTimelineTextStep;
  isCompleted: boolean;
  isCurrent: boolean;
  isLast: boolean;
}) {
  return (
    <div>
      <p className="font-mono text-sm font-semibold text-text-primary mt-2">
        {step.title}
      </p>
      {isCompleted && (
        <div className="flex items-center gap-1.5 mt-1">
          <Icons.Check className="size-3.5 text-text-success shrink-0" />
          <span className="font-geist-mono text-sm font-normal text-text-secondary">
            {step.description}
          </span>
        </div>
      )}
      {isCurrent && !isCompleted && (
        <div className="flex items-center gap-1.5 mt-1">
          <span className="font-geist-mono text-sm font-normal text-text-tertiary">
            In progress...
          </span>
        </div>
      )}
      {!isLast && (
        <div className="ml-[6px] mt-1 mb-1 h-3 border-l border-border-secondary" />
      )}
    </div>
  );
}

export function CompletedQuestionStep({
  step,
  isLast,
}: {
  step: TDemoAIFillingSubstep & { type: 'question' };
  isLast: boolean;
}) {
  return (
    <div>
      <p className="font-mono text-sm font-semibold text-text-primary mt-2">
        {step.title}
      </p>
      <div className="flex items-center gap-1.5 mt-1">
        <Icons.Check className="size-3.5 text-text-success shrink-0" />
        <span className="font-geist-mono text-sm font-normal text-text-secondary">
          Approved
        </span>
      </div>
      {!isLast && (
        <div className="ml-[6px] mt-1 mb-1 h-3 border-l border-border-secondary" />
      )}
    </div>
  );
}

export function TimelineView({
  substeps,
  currentIndex,
  answers,
}: {
  substeps: TDemoAIFillingSubstep[];
  currentIndex: number;
  answers: Record<number, string>;
}) {
  const visibleSteps = substeps
    .map((step, idx) => ({ step, idx }))
    .filter(({ idx }) => idx <= currentIndex);

  return (
    <div>
      <div className="flex items-center gap-1.5 py-1.5">
        <div className="flex items-center justify-center h-8 w-8 overflow-hidden shrink-0">
          <Lottie
            animationData={commendaLoadingAnimation}
            loop
            autoplay
            style={{ width: 96, height: 96, transform: 'scale(3)' }}
          />
        </div>
        <span className="txt-small-medium text-bg-brand-solid">
          Working...
        </span>
      </div>

      <div className="ml-1 space-y-0">
        {visibleSteps.map(({ step, idx }, visibleIdx) => {
          const isCompleted = idx < currentIndex;
          const isCurrent = idx === currentIndex;
          const isLastVisible = visibleIdx === visibleSteps.length - 1;

          if (step.type === 'question') {
            if (answers[idx] === undefined) return null;
            return (
              <CompletedQuestionStep
                key={`question-${step.title}`}
                step={step}
                isLast={isLastVisible}
              />
            );
          }

          return (
            <TextStepItem
              key={`text-${step.title}`}
              step={step}
              isCompleted={isCompleted}
              isCurrent={isCurrent}
              isLast={isLastVisible}
            />
          );
        })}
      </div>
    </div>
  );
}

export function TextOnlyTimelineView({
  steps,
  currentIndex,
}: {
  steps: TTimelineTextStep[];
  currentIndex: number;
}) {
  const visibleSteps = steps
    .map((step, idx) => ({ step, idx }))
    .filter(({ idx }) => idx <= currentIndex);

  return (
    <div>
      <div className="flex items-center gap-1.5 py-1.5">
        <div className="flex items-center justify-center h-8 w-8 overflow-hidden shrink-0">
          <Lottie
            animationData={commendaLoadingAnimation}
            loop
            autoplay
            style={{ width: 96, height: 96, transform: 'scale(3)' }}
          />
        </div>
        <span className="txt-small-medium text-bg-brand-solid">
          Working...
        </span>
      </div>

      <div className="ml-1 space-y-0">
        {visibleSteps.map(({ step, idx }, visibleIdx) => (
          <TextStepItem
            key={`text-${step.title}`}
            step={step}
            isCompleted={idx < currentIndex}
            isCurrent={idx === currentIndex}
            isLast={visibleIdx === visibleSteps.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

export function CompletedTimelineView({
  substeps,
}: {
  substeps: TDemoAIFillingSubstep[];
}) {
  return (
    <div className="ml-1 space-y-0">
      {substeps.map((step, idx) => {
        const isLast = idx === substeps.length - 1;

        if (step.type === 'question') {
          return (
            <CompletedQuestionStep
              key={`question-${step.title}`}
              step={step}
              isLast={isLast}
            />
          );
        }

        return (
          <TextStepItem
            key={`text-${step.title}`}
            step={step}
            isCompleted
            isCurrent={false}
            isLast={isLast}
          />
        );
      })}
    </div>
  );
}

export function CompletedTextTimelineView({
  steps,
}: {
  steps: TTimelineTextStep[];
}) {
  return (
    <div className="ml-1 space-y-0">
      {steps.map((step, idx) => (
        <TextStepItem
          key={`text-${step.title}`}
          step={step}
          isCompleted
          isCurrent={false}
          isLast={idx === steps.length - 1}
        />
      ))}
    </div>
  );
}

type TimelineEvent = {
  type: 'title' | 'substep';
  groupIndex: number;
  substepIndex?: number;
  text: string;
  delayMs: number;
};

function flattenGroupedSteps(steps: TDemoAIFillingGroupedStep[]): TimelineEvent[] {
  const events: TimelineEvent[] = [];
  steps.forEach((step, gi) => {
    events.push({
      type: 'title',
      groupIndex: gi,
      text: step.title,
      delayMs: step.titleDelayMs ?? 3000,
    });
    step.substeps.forEach((sub, si) => {
      events.push({
        type: 'substep',
        groupIndex: gi,
        substepIndex: si,
        text: sub,
        delayMs: step.substepDelayMs ?? 2000,
      });
    });
  });
  return events;
}

export function AnimatedGroupedTimeline({
  steps,
  initialCompletedGroups = [],
}: {
  steps: TDemoAIFillingGroupedStep[];
  initialCompletedGroups?: TDemoAIFillingGroupedStep[];
}) {
  const allEvents = useMemo(() => flattenGroupedSteps(steps), [steps]);
  const [revealedCount, setRevealedCount] = useState(1);
  const [historyOpen, setHistoryOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const revealedRef = useRef(1);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const clockRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    setRevealedCount(1);
    setHistoryOpen(false);
    setElapsedSeconds(0);
    revealedRef.current = 1;

    clockRef.current = setInterval(() => {
      setElapsedSeconds((s) => s + 1);
    }, 1000);

    function scheduleNext() {
      const nextIdx = revealedRef.current;
      if (nextIdx >= allEvents.length) return;
      const nextEvent = allEvents[nextIdx];
      timerRef.current = setTimeout(() => {
        revealedRef.current = nextIdx + 1;
        setRevealedCount(nextIdx + 1);
        scheduleNext();
      }, nextEvent.delayMs);
    }

    scheduleNext();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (clockRef.current) clearInterval(clockRef.current);
    };
  }, [allEvents]);

  const formatElapsed = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const visibleEvents = allEvents.slice(0, revealedCount);

  let currentGroupIndex = 0;
  visibleEvents.forEach((ev) => {
    if (ev.type === 'title') currentGroupIndex = ev.groupIndex;
  });

  const completedGroups: TDemoAIFillingGroupedStep[] = [
    ...initialCompletedGroups,
  ];
  for (let gi = 0; gi < currentGroupIndex; gi++) {
    completedGroups.push(steps[gi]);
  }

  const currentGroup = steps[currentGroupIndex];
  const currentSubsteps: string[] = [];
  visibleEvents.forEach((ev) => {
    if (ev.type === 'substep' && ev.groupIndex === currentGroupIndex) {
      currentSubsteps.push(ev.text);
    }
  });

  return (
    <div className="relative">
      <style>{`
        @keyframes timeline-fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .timeline-enter { animation: timeline-fadeIn 0.5s ease-in-out both; }
        @keyframes timeline-shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        .shiny-text {
          background: linear-gradient(
            90deg,
            currentColor 0%,
            currentColor 47%,
            rgba(180, 180, 180, 0.6) 50%,
            currentColor 53%,
            currentColor 100%
          );
          background-size: 200% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: timeline-shimmer 6s ease-in-out infinite;
        }
      `}</style>

      <div className="absolute top-0 left-0 right-0 h-4 bg-gradient-to-b from-white to-transparent z-10 pointer-events-none rounded-t-md" />
      <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-white to-transparent z-10 pointer-events-none rounded-b-md" />

      <div className="py-1 px-1" style={{ fontFamily: "'Geist Mono', monospace" }}>
        <div className="flex items-center gap-0 mb-1.5">
          <div className="flex items-center justify-center h-12 w-12 overflow-hidden shrink-0">
            <Lottie
              animationData={commendaLoadingAnimation}
              loop
              autoplay
              style={{ width: 144, height: 144, transform: 'scale(3)' }}
            />
          </div>
          <span className="text-xs font-bold flex-1" style={{ color: 'rgb(76, 163, 218)', fontFamily: 'Mulish, sans-serif' }}>
            Working...
          </span>
          <span className="text-[10px] font-mono text-text-tertiary tabular-nums">
            {formatElapsed(elapsedSeconds)}
          </span>
        </div>

        <div className="border-l border-border-secondary ml-[3px] pl-3">
          {completedGroups.length > 0 && (
            <Collapsible open={historyOpen} onOpenChange={setHistoryOpen}>
              <CollapsibleTrigger className="flex items-center gap-1.5 w-full cursor-pointer mb-2">
                <Icons.ChevronDown
                  className={`size-3 text-text-tertiary transition-transform duration-200 ${
                    historyOpen ? 'rotate-0' : '-rotate-90'
                  }`}
                />
                <span className="text-[10px] text-text-secondary">
                  {completedGroups.length} step{completedGroups.length > 1 ? 's' : ''} completed
                </span>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="flex flex-col gap-3 mb-3">
                  {completedGroups.map((group, gi) => (
                    <div key={gi}>
                      <p className="text-sm font-semibold leading-5 text-text-primary" style={{ fontFamily: 'Mulish, sans-serif' }}>
                        {group.title}
                      </p>
                      <div className="mt-1 ml-[3px] border-l border-border-secondary">
                        {group.substeps.map((sub) => (
                          <div key={sub} className="flex items-start py-0.5">
                            <div className="w-3 h-px bg-border-secondary shrink-0 mt-[7px]" />
                            <Icons.Check className="size-2.5 text-text-success shrink-0 ml-1 mt-[3px]" />
                            <span className="text-[10px] text-text-tertiary ml-1.5">
                              {sub}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}

          {currentGroup && (
            <div className="timeline-enter" key={currentGroupIndex}>
              <p className="text-sm font-semibold leading-5 shiny-text" style={{ fontFamily: 'Mulish, sans-serif', color: 'var(--text-primary)' }}>
                {currentGroup.title}
              </p>
              {currentSubsteps.length > 0 && (
                <div className="mt-1 ml-[3px] border-l border-border-secondary">
                  {currentSubsteps.map((text, si) => (
                    <div
                      key={si}
                      className="flex items-start py-0.5 timeline-enter"
                      style={{ animationDelay: `${si * 80}ms` }}
                    >
                      <div className="w-3 h-px bg-border-secondary shrink-0 mt-[7px]" />
                      <Icons.Check className="size-2.5 text-text-success shrink-0 ml-1 mt-[3px]" />
                      <span className="text-[10px] text-text-tertiary ml-1.5">
                        {text}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function CompletedGroupedTimelineView({
  steps,
}: {
  steps: TDemoAIFillingGroupedStep[];
}) {
  return (
    <div className="py-1 px-1" style={{ fontFamily: "'Geist Mono', monospace" }}>
      <div className="flex flex-col gap-3">
        {steps.map((group, gi) => (
          <div key={gi}>
            <p className="text-sm font-semibold leading-5 text-text-primary" style={{ fontFamily: 'Mulish, sans-serif' }}>
              {group.title}
            </p>
            <div className="mt-1 ml-[3px] border-l border-border-secondary">
              {group.substeps.map((sub) => (
                <div key={sub} className="flex items-start py-0.5">
                  <div className="w-3 h-px bg-border-secondary shrink-0 mt-[7px]" />
                  <Icons.Check className="size-2.5 text-text-success shrink-0 ml-1 mt-[3px]" />
                  <span className="text-[10px] text-text-tertiary ml-1.5">
                    {sub}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function CollapsibleHistory({
  children,
}: {
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center gap-1.5 py-2 w-full group cursor-pointer">
        <Icons.CheckCircle className="size-4 text-text-success shrink-0" />
        <span className="font-geist-mono text-sm font-semibold text-text-primary">
          Completed
        </span>
        <Icons.ChevronDown
          className={`size-4 text-text-tertiary transition-transform duration-200 ml-auto ${open ? 'rotate-180' : ''
            }`}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>{children}</CollapsibleContent>
    </Collapsible>
  );
}
